import { useCallback, useContext, useEffect, useState } from 'react';

import { getFeatureFlags, updateSkillFeatureFlags, updateUserFeatureFlags } from '../api';
import { CurrentAliceAppContext } from '../context/aliceAppContext';
import { FeatureFlags } from '../types';

interface IUserFlagDTO {
    key: string;
    value: boolean;
}

export const useFeatureFlags = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [skillFlags, setSkillFlags] = useState<string[]>([]);
    const [userFlags, setUserFlags] = useState<IUserFlagDTO[]>([]);

    const context = useContext(CurrentAliceAppContext);

    const skillId = context?.aliceApp?.id || '';
    const userId = context?.aliceApp?.user?.id || context?.aliceApp?.userId || '';
    const channel = context?.aliceApp?.channel;

    const setFetchedState = useCallback(
        (currentSkillId: string, response: FeatureFlags) => {
            if (currentSkillId !== skillId) {
                return;
            }
            const updatedSkills = (response?.skill_feature_flags || []).sort();
            const updatedUserFlags = Object.entries(response.user_feature_flags || {})
                .map(([key, value]) => ({ key, value }))
                .sort((a, b) => a.key.localeCompare(b.key));
            setSkillFlags(updatedSkills);
            setUserFlags(updatedUserFlags);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [skillId, channel],
    );

    useEffect(() => {
        let ignore = false;
        if (skillId && channel) {
            getFeatureFlags(skillId, channel)
                .then((response) => {
                    if (!ignore) {
                        setFetchedState(skillId, response);
                        setIsLoading(false);
                    }
                })
                .catch((error) => {
                    console.error(error?.message);
                    setIsLoading(false);
                });
        }
        return () => {
            ignore = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skillId, channel]);

    const addSkillFlag = () => {
        setSkillFlags((skillFlags) => [...skillFlags, '']);
    };

    const updateSkillFlag = (flagIndex: number, updatedFlag: string | null) => {
        const updatedSkills = [...skillFlags];
        if (updatedFlag !== null) {
            updatedSkills[flagIndex] = updatedFlag.trim();
        } else {
            updatedSkills.splice(flagIndex, 1);
        }
        setSkillFlags(updatedSkills);
    };

    const addUserFlag = () => {
        setUserFlags([...userFlags, { key: '', value: true }]);
    };

    const updateUserFlag = (flagIndex: number, updatedFlagKey: string | null, updatedFlagValue: boolean) => {
        const updatedUserFlags = [...userFlags];
        if (updatedFlagKey !== null) {
            updatedUserFlags[flagIndex] = { key: updatedFlagKey.trim(), value: updatedFlagValue };
        } else {
            updatedUserFlags.splice(flagIndex, 1);
        }
        setUserFlags(updatedUserFlags);
    };

    const saveFlags = async () => {
        if (!skillId || !channel) return;
        setIsLoading(true);
        const skillFlagsFiltered = skillFlags.filter((flag) => !!flag.trim());
        const filteredUserFlags = userFlags
            .filter(({ key }) => !!key)
            .reduce<Record<string, boolean>>((acc, curr) => {
                acc[curr.key] = curr.value;
                return acc;
            }, {});
        Promise.all([
            updateSkillFeatureFlags(skillId, channel, skillFlagsFiltered),
            updateUserFeatureFlags(userId, channel, filteredUserFlags),
        ])
            .then(() => getFeatureFlags(skillId, channel))
            .then((response) => {
                setFetchedState(skillId, response);
                setIsLoading(false);
            })
            .catch((error) => {
                console.error(error?.message);
                setIsLoading(false);
            });
    };

    return {
        isLoading,
        skillFlags,
        addSkillFlag,
        updateSkillFlag,
        userFlags,
        addUserFlag,
        updateUserFlag,
        saveFlags,
    };
};
